/**
 * MarkdownBody — native HTML rendering with full web parity.
 *
 * Pipeline (same as web):
 *   1. parseResponseWithCharts()  → split markdown vs chart blocks
 *   2. markdownToHtml()           → same regex pipeline as web utilities.js
 *   3. react-native-render-html   → native rendering, no WebView
 *
 * Architecture:
 *   TRenderEngineProvider (stable: tagsStyles, classesStyles, baseStyle)
 *     └─ RenderHTMLFragment (updates per streaming delta — no engine restart)
 *
 * This eliminates the "TRenderEngineProvider re-render" warning that fires
 * when RenderHtml (which bundles its own provider) receives frequent source
 * updates during streaming.
 */

import React, { useMemo } from 'react';
import { View, Text, useWindowDimensions, Linking } from 'react-native';
import {
  TRenderEngineProvider,
  RenderHTMLConfigProvider,
  RenderHTMLSource,
} from 'react-native-render-html';
import { useTheme } from '../hooks/useTheme';
import { extractVaultLinkCode, resolveAndOpenVaultLink } from '../utilities/vaultLink';
import { markdownToHtml } from '../utilities/markdownToHtml';
import { parseResponseWithCharts } from '../utilities/parseResponseWithCharts';
import NativeTable from './NativeTable';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore – ChartView.tsx exists; Expo TS resolution quirk
import ChartView from './ChartView';

interface MarkdownBodyProps {
  children: string;
  streaming?: boolean;
}

function stripCaret(text: string) {
  return text.endsWith('▌') ? text.slice(0, -1) : text;
}

const SYSTEM_FONTS = ['System', 'Menlo', 'Courier New', 'monospace'];

// Tables are intercepted before RNRH via splitHtmlByTables() and rendered by
// NativeTable, so RNRH never sees <table> elements. No custom table renderer needed.

type HtmlSegment = { type: 'html' | 'table'; content: string };

/**
 * Split an HTML string at the <div style="overflow-x:auto..."><table>...</table></div>
 * wrappers that markdownToHtml places around every table block.  "div" is in the
 * sanitizer's whitelist so this wrapper survives the sanitization pass intact.
 * The captured content is just the inner <table>...</table> string.
 */
function splitHtmlByTables(html: string): HtmlSegment[] {
  const segments: HtmlSegment[] = [];
  const re = /<div style="overflow-x:auto[^"]*"[^>]*>(<table[\s\S]*?<\/table>)<\/div>/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    if (m.index > last) {
      segments.push({ type: 'html', content: html.slice(last, m.index) });
    }
    segments.push({ type: 'table', content: m[1] });
    last = m.index + m[0].length;
  }
  if (last < html.length) {
    segments.push({ type: 'html', content: html.slice(last) });
  }
  return segments;
}

export default function MarkdownBody({ children, streaming = false }: MarkdownBodyProps) {
  const theme = useTheme();
  const { width } = useWindowDimensions();

  // Re-build styles only when the colour scheme changes, not on every render.
  const { tagsStyles, classesStyles, baseStyle, renderersProps } = useMemo(
    () => buildHtmlStyles(theme),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme.dark]
  );

  const segments = useMemo(
    () => parseResponseWithCharts(children || '', true, streaming),
    [children, streaming]
  );

  return (
    // TRenderEngineProvider — stable engine config (tagsStyles, fonts).
    // RenderHTMLConfigProvider — stable renderer config (renderersProps, flags).
    // RenderHTMLSource — only this re-renders on every streaming delta.
    // This prevents the "TRenderEngineProvider re-render" warning during streaming.
    <TRenderEngineProvider
      tagsStyles={tagsStyles as any}
      classesStyles={classesStyles as any}
      baseStyle={baseStyle as any}
      systemFonts={SYSTEM_FONTS}
    >
      <RenderHTMLConfigProvider
        renderersProps={renderersProps as any}
        enableExperimentalBRCollapsing
        enableExperimentalGhostLinesPrevention
      >
        <View>
          {segments.map((seg, i) => {
            const isLast = i === segments.length - 1;

            if (seg.type === 'markdown') {
              const raw = isLast && streaming
                ? `${stripCaret(seg.content || '')}▌`
                : (seg.content || '');
              if (!raw.trim()) return null;

              const html = markdownToHtml(raw, { streaming });
              const htmlSegs = splitHtmlByTables(html);

              return (
                <View key={i}>
                  {htmlSegs.map((hs, j) => {
                    if (hs.type === 'table') {
                      return <NativeTable key={j} html={hs.content} />;
                    }
                    if (!hs.content.trim()) return null;
                    return (
                      <RenderHTMLSource
                        key={j}
                        contentWidth={width}
                        source={{ html: hs.content }}
                      />
                    );
                  })}
                </View>
              );
            }

            if (seg.type === 'chart' && seg.chartPayload) {
              return <ChartView key={i} chartPayload={seg.chartPayload} />;
            }

            if (seg.type === 'chart-pending') {
              return (
                <View
                  key={i}
                  style={{
                    padding: 10,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderStyle: 'dashed',
                    borderColor: theme.colors.border,
                    marginVertical: 4,
                  }}
                >
                  <Text style={{ color: theme.colors.muted, fontSize: 13 }}>Loading chart…</Text>
                </View>
              );
            }

            return null;
          })}
        </View>
      </RenderHTMLConfigProvider>
    </TRenderEngineProvider>
  );
}

// ─── HTML styles (mirrors web's .markdown-body CSS) ───────────────────────────

function buildHtmlStyles(theme: ReturnType<typeof import('../hooks/useTheme').useTheme>) {
  const c = theme.colors;

  // Web uses headerTextColor as the markdown body base colour (#696B74 in light mode).
  const bodyColor = c.headerText;

  const baseStyle = {
    color: bodyColor,
    fontSize: 15,
    lineHeight: 24, // 1.6 × 15 — matches web p line-height: 1.6
  };

  const tagsStyles = {
    // ── Paragraphs — web: margin 0 0 0.75em 0; line-height 1.6
    p: {
      color: bodyColor,
      fontSize: 15,
      lineHeight: 24,
      marginTop: 0,
      // Reduced from 11 → 8. RNRH doesn't have CSS-style "adjacent
      // sibling" margin collapse, so the p.marginBottom + the next
      // element's marginTop stack additively. The previous values
      // produced ~51px of vertical air between a paragraph end and
      // the next ul's first chip (11 + 20 h2.marginTop + 8
      // h2.marginBottom + 12 ul.marginTop) — visible as a "big gap"
      // above the document-link chip in real-world responses.
      marginBottom: 8,
    },

    // ── Headings — borderless, tighter rhythm.
    // The chat-bubble card now provides the visual frame, so the
    // old border-bottom on h1/h2 (which mimicked GitHub README
    // styling) reads as redundant chrome inside an already-framed
    // card. Drop the borders; lean on weight + spacing + letter
    // tracking to differentiate the hierarchy.
    h1: {
      color: c.headerText,
      fontSize: 22,
      fontWeight: '700' as const,
      lineHeight: 28,
      marginTop: 4,
      marginBottom: 8,
      letterSpacing: -0.3,
    },
    h2: {
      color: c.headerText,
      fontSize: 17,
      fontWeight: '700' as const,
      lineHeight: 22,
      // Tightened — 20/8 stacked with the surrounding p/ul margins
      // produced ~50px of dead space above each h2-led section
      // ("Document Links", "Medication History"). 10/4 keeps clear
      // section separation without the void.
      marginTop: 10,
      marginBottom: 4,
      letterSpacing: -0.2,
    },
    h3: {
      color: c.muted,
      fontSize: 13,
      fontWeight: '600' as const,
      lineHeight: 18,
      marginTop: 16,
      marginBottom: 6,
      letterSpacing: 0.4,
      textTransform: 'uppercase' as const,
    },
    h4: {
      color: c.headerText,
      fontSize: 15,
      fontWeight: '600' as const,
      marginTop: 10,
      marginBottom: 5,
    },
    h5: {
      color: c.headerText,
      fontSize: 14,
      fontWeight: '600' as const,
      marginTop: 8,
      marginBottom: 4,
    },
    h6: {
      color: c.headerText,
      fontSize: 13,
      fontWeight: '600' as const,
      marginTop: 6,
      marginBottom: 3,
    },

    // ── Lists — clean typographic bullets (not boxy cards).
    // Each <li> used to render as a white "fact-card" pill (background +
    // radius + shadow), which read as heavy/boxy in a chat summary. We drop
    // the card chrome entirely and let the brand-tinted bullet glyph (inlined
    // as `<strong>•</strong>` by markdownToHtml, colored via tagsStyles.strong)
    // carry the visual rhythm — a lighter, more contemporary list.
    //
    // A small ul/li left gutter replaces the old in-pill padding so bullets
    // sit just off the margin and nested `<ul>`s still step in under their
    // parent. ol keeps a wider gutter for its numbers.
    ul: { marginTop: 4, marginBottom: 12, paddingLeft: 2 },
    ol: { marginTop: 4, marginBottom: 12, paddingLeft: 18 },
    // fontWeight 'normal' prevents the bullet marker from inheriting bold
    // weight when <li> contains only <strong> children (RNRH v6 behaviour).
    li: {
      color: bodyColor,
      fontSize: 15,
      lineHeight: 23,
      marginBottom: 5,
      fontWeight: 'normal' as const,
      paddingLeft: 4,
    },

    // ── Inline code — web: background coreBackground
    code: {
      backgroundColor: c.coreBackground,
      color: bodyColor,
      fontFamily: 'Menlo',
      fontSize: 13,
      paddingLeft: 4,
      paddingRight: 4,
      borderRadius: 3,
    },

    // ── Code block
    pre: {
      backgroundColor: c.coreBackground,
      borderRadius: 6,
      padding: 12,
      marginTop: 8,
      marginBottom: 12,
      overflow: 'hidden' as const,
    },

    // ── Blockquote — web: border-left faintBorder; italic; background coreBackground
    blockquote: {
      borderLeftWidth: 4,
      borderLeftColor: c.border,
      paddingLeft: 12,
      marginTop: 12,
      marginBottom: 12,
      color: bodyColor,
      backgroundColor: c.coreBackground,
      fontStyle: 'italic' as const,
    },

    // ── Links — web: buttonBackground colour (= primary)
    a: {
      color: c.primary,
      textDecorationLine: 'none' as const,
    },

    // ── Horizontal rule
    hr: {
      backgroundColor: c.border,
      height: 1,
      marginTop: 24,
      marginBottom: 24,
    },

    // ── Bold — web: font-weight 600; color buttonBackground (= primary)
    strong: {
      fontWeight: '600' as const,
      color: c.primary,
    },
    em: {
      fontStyle: 'italic' as const,
    },
    // Underline (from __text__)
    u: {
      textDecorationLine: 'underline' as const,
    },
    // Strikethrough
    s: {
      textDecorationLine: 'line-through' as const,
    },

    // ── Tables
    table: {
      borderWidth: 1,
      borderColor: c.border,
      marginTop: 12,
      marginBottom: 12,
    },
    thead: {
      backgroundColor: c.coreBackground,
    },
    th: {
      backgroundColor: c.coreBackground,
      color: bodyColor,
      fontWeight: '600' as const,
      fontSize: 13,
      padding: 8,
      borderWidth: 1,
      borderColor: c.border,
    },
    td: {
      color: bodyColor,
      fontSize: 13,
      padding: 8,
      borderWidth: 1,
      borderColor: c.border,
    },
    tr: {
      borderBottomWidth: 1,
      borderBottomColor: c.border,
    },
  };

  // CSS class-based styles (mirrors the .strong-title class in web)
  const classesStyles = {
    // Bold text ending with ":" — e.g. **Summary:** — web: same colour/weight as strong
    'strong-title': {
      color: c.primary,
      fontWeight: '600' as const,
    },
    // Streaming table placeholder while the table is being built
    'streaming-table-placeholder': {
      color: c.muted,
      fontSize: 13,
      fontStyle: 'italic' as const,
    },
    // Inline bullet glyph at the start of every <li>, injected by
    // markdownToHtml as `<strong class="li-bullet">•</strong>` for
    // sub-items and `<strong class="li-bullet-top">•</strong>` for
    // top-level. Wrapped in `<strong>` so it inherits the brand
    // `primary` color from `tagsStyles.strong`.
    //
    // U+2022 BULLET (`•`) is naturally x-height-centered by the font
    // design, so no vertical-align gymnastics. (Earlier `●` plus
    // `verticalAlign: 'middle'` never worked — RNW silently drops
    // verticalAlign from RN style objects; DevTools confirmed the
    // rendered span always reported `vertical-align: baseline`.)
    //
    // `marginRight: 10` is the only style RNW reliably forwards to
    // the bullet span — it produces the gap between glyph and text.
    'li-bullet': {
      marginRight: 10,
    },
    // Top-level bullet — slightly larger than sub-bullets so the
    // parent items ("Communication:", "Daily Living:", section
    // labels) read as headers within the chip stack.
    'li-bullet-top': {
      marginRight: 10,
      fontSize: 18,
    },
    // Document-link bullet — paperclip 📎 instead of •, used for li
    // rows whose content is a single `<a href>` (e.g. "Document Links
    // → poor-audio.ogg"). Sized down a touch from body to match the
    // visual weight of the typographic bullet — emoji are wider than
    // text glyphs at the same point.
    'li-bullet-link': {
      marginRight: 8,
      fontSize: 13,
    },
  };

  // Renderer props — control bullet/number marker appearance.
  //
  // Markers tuned to be light and unobtrusive: muted color, smaller
  // than body text, narrow box so they sit close to the pill without
  // taking visual weight. The prior approach (injecting an inline
  // `<span class="li-marker">●</span>` from markdownToHtml) ended up
  // rendering as literal text in some RNRH/RNW configurations — the
  // markup wasn't stripped — so we lean on RNRH's native marker again
  // and just dial it down visually.
  //
  // UL markers HIDDEN — the bullet glyph is inlined as
  // `<strong>●</strong>` at the start of every li by markdownToHtml.
  // Leaving the default marker on would double up: one brand-tinted
  // dot inside the pill + a second muted dot sitting outside.
  //
  // OL markers stay brand-tinted since enumeration carries meaning
  // ("the third item") and benefits from being visually anchored.
  const ulMarkerTextStyle = {
    color: 'transparent',
    opacity: 0,
    fontSize: 0,
    lineHeight: 0,
  };
  const ulMarkerBoxStyle = {
    width: 0,
    minWidth: 0,
    paddingRight: 0,
  };
  const olMarkerTextStyle = {
    color: c.primary,
    fontSize: 14,
    lineHeight: 23,
    fontWeight: '700' as const,
  };
  const olMarkerBoxStyle = {
    paddingRight: 8,
    minWidth: 18,
  };
  const renderersProps = {
    ul: { markerTextStyle: ulMarkerTextStyle, markerBoxStyle: ulMarkerBoxStyle },
    ol: { markerTextStyle: olMarkerTextStyle, markerBoxStyle: olMarkerBoxStyle },
    a: {
      // Intercept vault-item links so they open the credential's sheet in place
      // instead of launching a browser. All other links keep default behavior.
      onPress: (_event: any, href: string) => {
        const code = extractVaultLinkCode(href);
        if (code) {
          resolveAndOpenVaultLink(code);
          return;
        }
        if (href) Linking.openURL(href).catch(() => {});
      },
    },
  };

  return { tagsStyles, classesStyles, baseStyle, renderersProps };
}
